import { apiDelete, apiGet, apiPost } from '.'
import type { Message } from './messages'

export interface ThreadIdentity {
  id: number
  platform: string
  display_name: string
}

export interface ThreadSummary {
  contact_id: number
  contact_name: string
  latest_message_id: number
  latest_content: string
  latest_platform: string
  latest_received_at: string
  latest_received_at_iso_utc?: string | null
  latest_received_at_epoch?: number | null
  unread_count: number
  has_flagged?: boolean
  has_on_air?: boolean
  hasFlagged?: boolean
  hasOnAir?: boolean
  latest_flagged_message_id?: number | null
  latest_on_air_message_id?: number | null
  latestFlaggedMessageId?: number | null
  latestOnAirMessageId?: number | null
  latest_is_autoresponder?: boolean
  latestIsAutoresponder?: boolean
  latest_auto_rule_id?: number | null
  latestAutoRuleId?: number | null
  latest_auto_rule_name?: string | null
  latestAutoRuleName?: string | null
  identities: ThreadIdentity[]
}

export interface ThreadStatusSummary {
  thread_id: number
  has_flagged?: boolean
  has_on_air?: boolean
  hasFlagged?: boolean
  hasOnAir?: boolean
  latest_flagged_message_id?: number | null
  latest_on_air_message_id?: number | null
  latestFlaggedMessageId?: number | null
  latestOnAirMessageId?: number | null
}

export interface ThreadMessageStatusResponse {
  ok: boolean
  message: Message
  thread: ThreadStatusSummary
}

export interface ThreadStatusClearResponse {
  ok: boolean
  cleared: number
  message_ids?: number[]
  thread: ThreadStatusSummary
}

export interface ListThreadsResponse {
  items: ThreadSummary[]
  next_cursor: number | null
  total_unread_threads: number
}

export interface ListThreadsParams {
  stationId: number
  cursor?: number | null
  dir?: 'older' | 'newer'
  limit?: number
  platforms?: string[]
  intents?: string[]
  unreadOnly?: boolean
  flaggedOnly?: boolean
  onAirOnly?: boolean
  search?: string
  includeBlocked?: boolean
}

const toCsv = (values?: string[]): string | undefined => {
  if (!values || values.length === 0) {
    return undefined
  }
  return values.join(',')
}

export async function listThreads(
  params: ListThreadsParams,
  signal?: AbortSignal,
): Promise<ListThreadsResponse> {
  const {
    stationId,
    cursor,
    dir,
    limit,
    platforms,
    intents,
    unreadOnly,
    flaggedOnly,
    onAirOnly,
    search,
    includeBlocked,
  } = params

  const searchParams = new URLSearchParams()
  searchParams.set('station_id', String(stationId))

  if (cursor) {
    searchParams.set('cursor', String(cursor))
  }

  if (dir) {
    searchParams.set('dir', dir)
  }

  if (limit) {
    searchParams.set('limit', String(limit))
  }

  const platformCsv = toCsv(platforms)
  if (platformCsv) {
    searchParams.set('filter_platform', platformCsv)
  }

  const intentsCsv = toCsv(intents)
  if (intentsCsv) {
    searchParams.set('filter_intent', intentsCsv)
  }

  if (unreadOnly) {
    searchParams.set('filter_unread', '1')
  }

  if (flaggedOnly) {
    searchParams.set('filter_flagged', '1')
  }

  if (onAirOnly) {
    searchParams.set('filter_on_air', '1')
  }

  const trimmedSearch = search?.trim()
  if (trimmedSearch) {
    searchParams.set('q', trimmedSearch)
  }

  if (includeBlocked) {
    searchParams.set('include_blocked', '1')
  }

  const query = searchParams.toString()
  const path = query ? `threads?${query}` : 'threads'

  if (typeof window !== 'undefined' && window.__RPA_DEBUG?.net) {
    console.log('[threads] list', {
      stationId,
      cursor,
      dir,
      limit,
      platforms,
      intents,
      unreadOnly,
      search,
      includeBlocked,
    })
  }

  return apiGet<ListThreadsResponse>(path, { signal, cache: 'no-store' })
}

const withStationQuery = (path: string, stationId: number): string => {
  const [pathname, existingQuery = ''] = path.split('?')
  const search = new URLSearchParams(existingQuery)
  search.set('station_id', String(stationId))
  const query = search.toString()
  return query ? `${pathname}?${query}` : pathname
}

export async function markMessageOnAir(
  stationId: number,
  threadId: number,
  messageId: number,
): Promise<ThreadMessageStatusResponse> {
  const path = withStationQuery(`threads/${threadId}/messages/${messageId}/on-air`, stationId)
  return apiPost<ThreadMessageStatusResponse>(path)
}

export async function clearMessageOnAir(
  stationId: number,
  threadId: number,
  messageId: number,
): Promise<ThreadMessageStatusResponse> {
  const path = withStationQuery(`threads/${threadId}/messages/${messageId}/on-air`, stationId)
  return apiDelete<ThreadMessageStatusResponse>(path)
}

export async function markMessageFlagged(
  stationId: number,
  threadId: number,
  messageId: number,
): Promise<ThreadMessageStatusResponse> {
  const path = withStationQuery(`threads/${threadId}/messages/${messageId}/flag`, stationId)
  return apiPost<ThreadMessageStatusResponse>(path)
}

export async function clearMessageFlagged(
  stationId: number,
  threadId: number,
  messageId: number,
): Promise<ThreadMessageStatusResponse> {
  const path = withStationQuery(`threads/${threadId}/messages/${messageId}/flag`, stationId)
  return apiDelete<ThreadMessageStatusResponse>(path)
}

export async function clearThreadOnAir(
  stationId: number,
  threadId: number,
): Promise<ThreadStatusClearResponse> {
  const path = withStationQuery(`threads/${threadId}/on-air`, stationId)
  return apiDelete<ThreadStatusClearResponse>(path)
}

export async function clearThreadFlags(
  stationId: number,
  threadId: number,
): Promise<ThreadStatusClearResponse> {
  const path = withStationQuery(`threads/${threadId}/flags`, stationId)
  return apiDelete<ThreadStatusClearResponse>(path)
}
